import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Lightbulb, Users, Target, Heart, Shield } from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: "Innovation",
      description: "Creating smart solutions for real-world challenges through cutting-edge technology."
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Integrity",
      description: "Building trust through honesty, transparency, and ethical business practices."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Collaboration",
      description: "Working together with clients and partners for meaningful progress and success."
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Impact",
      description: "Turning innovative ideas into practical solutions that truly make a difference."
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Compassion",
      description: "Giving back to communities and people in need through our humanitarian initiatives."
    }
  ];

  const services = [
    {
      title: "Web Development",
      description: "Custom websites and web applications built with modern technologies for optimal performance and user experience.",
      slug: "web-development"
    },
    {
      title: "App Development", 
      description: "Native and cross-platform mobile applications designed to engage users and drive business growth.",
      slug: "app-development"
    },
    {
      title: "IT Consulting",
      description: "Strategic technology consulting to help businesses optimize their digital infrastructure and processes.",
      slug: "it-consulting"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-blue-50/30 to-yellow-50/20 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-vincotek-dark leading-tight">
                  Empowering Technology.{' '}
                  <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                    Enabling Growth.
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                  Vincotek builds digital solutions that make life and work easier for everyone. 
                  We're a technology startup focused on innovation, impact, and giving back to the community.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/about">
                  <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 group">
                    Learn More
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105">
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
                <img 
                  src="https://images.unsplash.com/photo-1594235048794-fae8583a5af5" 
                  alt="Modern tech office environment" 
                  className="w-full h-80 object-cover rounded-xl"
                />
                <div className="absolute -bottom-4 -left-4 bg-vincotek-yellow rounded-xl p-4 shadow-lg">
                  <div className="text-vincotek-dark text-sm font-semibold">Building the Future</div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-vincotek-blue to-vincotek-yellow rounded-full opacity-20 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do and define who we are as a company.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {features.map((feature, index) => {
              const valueLinks = [
                '/values/innovation',
                '/values/integrity', 
                '/values/collaboration',
                '/values/impact',
                '/values/compassion'
              ];
              
              return (
                <Link key={index} to={valueLinks[index]}>
                  <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-gray-50/50 cursor-pointer">
                    <CardContent className="p-6 text-center">
                      <div className="text-vincotek-blue mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-semibold text-vincotek-dark mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive technology solutions to help businesses thrive in the digital age.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <Link to={`/services/${service.slug}`}>
                    <Button variant="outline" className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300">
                      Learn More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/products-services">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's work together to create innovative solutions that drive growth and make a positive impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Start Your Project
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-vincotek-dark px-8 py-3 rounded-full transition-all duration-300">
                Learn About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;